// @flow

import type {
  AllVideosModel,
  VideoModel,
  VideoTopicModel,
  VideoPlaylistModel,
  ArticleVideoModel,
} from 'docs/data';
import type {
  VideosManifestModel,
  ArticleVideoRequestModel,
  VideosManifestModelVideo,
  VideosManifestModelTopic,
  VideosManifestModelPlaylist,
} from 'docs/types';

// NOTE(jasons): These "Providers" bridge the interface of `VideosManifestModel` and `ArticleVideoRequestModel`

interface VideosProvider {
  videos: {
    [string]: VideosManifestModelVideo,
  };
}

interface TopicsProvider extends VideosProvider {
  topics: {
    [string]: VideosManifestModelTopic,
  };
}

interface PlaylistsProvider extends TopicsProvider {
  playlists: {
    [string]: VideosManifestModelPlaylist,
  };
}

const transformVideo = (id: string, provider: VideosProvider): VideoModel => {
  const video = provider.videos[id];
  return {
    id: video.id,
    title: video.title,
    description: video.description,
    duration: video.duration,
    prefs: video.prefs,
    youtubeId: video.youtube_id,
    presenters: video.presenters,
    resources: video.resources,
    relatedDocs: video.related_docs,
  };
};

const transformTopic = (
  id: string,
  provider: TopicsProvider,
): VideoTopicModel => {
  const topic = provider.topics[id];

  return {
    id: topic.id,
    title: topic.title,
    description: topic.description,

    defaultVideo: {
      ...transformVideo(topic.default_video, provider),
      topicID: topic.id,
    },

    videos: topic.videos.map((videoId) => ({
      ...transformVideo(videoId, provider),
      topicID: topic.id,
    })),
  };
};

const transformPlaylist = (
  id: string,
  provider: PlaylistsProvider,
): VideoPlaylistModel => {
  const playlist = provider.playlists[id];

  const playlistChapters = playlist.chapters.map((chapter) => {
    if (chapter.topic_id) {
      return transformTopic(chapter.topic_id, provider);
    } else {
      throw new Error(`Cannot transform topic ${chapter.toString()}`);
    }
  });

  const videoPrefs = playlistChapters.reduce((acc, c) => {
    c.videos.forEach((v) => {
      Object.entries(v.prefs).forEach(([type, option]) => {
        if (acc[type]) {
          acc[type][option] = true;
        } else {
          acc[type] = {};
          acc[type][option] = true;
        }
      });
    });
    return acc;
  }, {});

  return {
    ...playlist,
    chapters: playlistChapters,
    prefs: videoPrefs,
  };
};

export const transformAllVideos = (
  manifest: VideosManifestModel,
): AllVideosModel => {
  const videos: {[string]: VideoModel} = {};
  const topics: {[string]: VideoTopicModel} = {};
  const playlists: {[string]: VideoPlaylistModel} = {};

  for (const id of Object.keys(manifest.videos)) {
    videos[id] = transformVideo(id, manifest);
  }

  for (const id of Object.keys(manifest.topics)) {
    topics[id] = transformTopic(id, manifest);
  }

  for (const id of Object.keys(manifest.playlists)) {
    playlists[id] = transformPlaylist(id, manifest);
  }

  return {videos, topics, playlists};
};

export const transformArticleVideo = (
  request: ?ArticleVideoRequestModel,
): ?ArticleVideoModel => {
  if (!request) {
    return;
  }

  const videos: Array<VideoModel> = [];

  for (const id of Object.keys(request.videos)) {
    videos.push(transformVideo(id, request));
  }

  if (request.topic) {
    const provider = {
      ...request,
      topics: {
        [request.topic.id]: request.topic,
      },
    };

    const topic = transformTopic(request.topic.id, provider);

    return {topic};
  } else if (videos.length > 0) {
    const video = videos[0];

    return {video};
  }
};
